import {
  get,
  keys,
  map,
  mapValues,
  merge,
  reduce,
  isEmpty,
  forEach,
  find,
  findIndex,
  isEqual,
  isUndefined
} from 'lodash';

import {
  FILTER_CLEARED,
  FILTERS_CLEARED,
  FILTERS_RESET,
  FILTERS_RESET_ALL,
  FILTERS_UPDATED,
  LOAD_FILTERS_SUCCESS,
  GET_FILTERS_CONFIG_SUCCESS,
  UPDATE_FILTER_OPTIONS
} from '../actions/filters-actions';

import {
  TOGGLE_SEASON_OPTIONS_DISABLED,
  SET_EPISODE_OPTIONS_LOADER,
  SHOW_SEASONS_SUCCESS,
  SHOW_CHARACTERS_SUCCESS
} from '../actions/show-actions';
import {
  SEGMENT_GET_SUCCESS,
  SEGMENT_SAVE_SUCCESS,
  SEGMENT_DELETE_SUCCESS,
  BENCHMARK_SELECTED
} from '../actions/audience-segment-actions';

const sortEpisodes = action =>
  action.options.sort((optionA, optionB) => {
    const [, seasonA, episodeA] = /S(\d+)\s*\|\s*E(\d+)/g.exec(optionA.label);
    const [, seasonB, episodeB] = /S(\d+)\s*\|\s*E(\d+)/g.exec(optionB.label);
    const valueA = parseInt(seasonA, 10) * 1000 + parseInt(episodeA, 10);
    const valueB = parseInt(seasonB, 10) * 1000 + parseInt(episodeB, 10);
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return optionA.label.localeCompare(optionB.label);
  });

const initialState = {
  groupings: [],
  fields: {},
  options: {},
  config: null,
  benchmarks: []
};

export default function filters(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FILTER_OPTIONS: {
      const options =
        action.filter === 'episode' ? sortEpisodes(action) : action.options;
      return {
        ...state,
        options: {
          ...state.options,
          [action.filter]: options
        },
        fields: {
          ...state.fields,
          [action.filter]: {
            ...state.fields[action.filter],
            options
          }
        }
      };
    }

    case LOAD_FILTERS_SUCCESS: {
      const groupings = map(
        action.filters,
        ({ id, label, filters: fs }) => ({ id, label, filters: keys(fs) }),
        []
      );

      const fields = reduce(
        action.filters,
        (acc, group) => merge(acc, group.filters),
        {}
      );

      const options = mapValues(fields, 'options');

      return { ...state, groupings, fields, options };
    }

    case FILTERS_UPDATED: {
      const updatedFields = mapValues(action.filters, (newValue, field) => {
        const filter = get(state, ['fields', field], {});
        const filterOptions = get(filter, ['options'], []);
        const booleanSeparator = get(action.booleanFilters, [field], 'OR');
        let value;

        if (!isUndefined(newValue)) {
          if (filter.isAsync) {
            if (isEqual(newValue, filter.value)) {
              value = filter.value;
            } else {
              value = map(newValue, v => {
                const filterValueWithLabel = find(
                  filterOptions,
                  filterOption =>
                    parseInt(v.value, 10) === parseInt(filterOption.value, 10)
                );
                return isEmpty(filterValueWithLabel) ? v : filterValueWithLabel;
              });
            }
          } else if (filter.id === 'date') {
            value = newValue; // TODO find a solution for this case
          } else {
            value = newValue;
          }
        }

        return {
          ...state.fields[field],
          value,
          booleanSeparator
        };
      });

      return {
        ...state,
        fields: { ...state.fields, ...updatedFields }
      };
    }

    case FILTER_CLEARED: {
      const field = {
        ...state.fields[action.id],
        value: null,
        booleanSeparator: 'OR'
      };

      return {
        ...state,
        fields: {
          ...state.fields,
          [action.id]: field
        }
      };
    }

    case FILTERS_CLEARED: {
      const { benchmark } = state.fields;
      const fields = mapValues(state.fields, field => ({
        ...field,
        value:
          action.omittedFields && action.omittedFields.includes(field.id)
            ? field.value
            : null,
        booleanSeparator: 'OR'
      }));

      return {
        ...state,
        fields: { ...fields, benchmark: benchmark || {} }
      };
    }

    case FILTERS_RESET_ALL:
      return {
        ...initialState
      };
    case FILTERS_RESET:
      return {
        ...initialState,
        config: state.config,
        benchmarks: state.benchmarks
      };
    case SHOW_SEASONS_SUCCESS: {
      if (!state.fields.season) {
        return state;
      }

      const fields = {
        ...state.fields,
        season: {
          ...state.fields.season,
          options: action.seasons
        }
      };

      const options = {
        ...state.options,
        season: action.seasons
      };

      return {
        ...state,
        fields,
        options
      };
    }

    case SHOW_CHARACTERS_SUCCESS: {
      return {
        ...state,
        options: {
          ...state.options,
          characterName: action.characters
        }
      };
    }

    case TOGGLE_SEASON_OPTIONS_DISABLED: {
      const fields = {
        ...state.fields,
        season: {
          ...state.fields.season,
          disabled: action.isDisabled,
          isDisabled: action.isDisabled
        },
        episode: {
          ...state.fields.episode,
          value: action.updateEpisodes
            ? action.episodes
            : get(state.fields, 'episode.value', null)
        }
      };

      return {
        ...state,
        fields
      };
    }

    case SET_EPISODE_OPTIONS_LOADER: {
      const seasons = get(action, 'seasons', []);
      const fields = {
        ...state.fields,
        episode: {
          ...state.fields.episode,
          disabled: false,
          isDisabled: false,
          loadOptions: action.loader,
          seasons: seasons
            ? get(action, 'seasons', [])
                .map(s => s.label)
                .join(',')
            : seasons
        },
        season: {
          ...state.fields.season,
          value: action.updateSeasons
            ? action.seasons
            : get(state.fields, 'season.value', null)
        }
      };

      return {
        ...state,
        fields
      };
    }

    case GET_FILTERS_CONFIG_SUCCESS: {
      const updatedFields = mapValues(action.config, (fieldObject, field) => {
        const filter = get(state, ['fields', field], {});
        const value = isEmpty(filter.value) ? null : filter.value;
        const booleanSeparator = !isEmpty(filter.booleanSeparator)
          ? filter.booleanSeparator
          : 'OR';

        return {
          ...fieldObject,
          value,
          booleanSeparator
        };
      });

      return {
        ...state,
        fields: { ...updatedFields },
        config: { ...action.config }
      };
    }

    case SEGMENT_DELETE_SUCCESS: {
      const { fields } = state;

      return { ...state, fields };
    }

    case SEGMENT_GET_SUCCESS: {
      const { fields, options, config } = state;
      const segments = action.segments.filter(s => !s.isBenchmark);
      const benchmarks = action.segments.filter(s => s.isBenchmark);

      fields.segments.options = segments;
      config.segments.options = segments;
      options.segments = segments;
      options.benchmarks = benchmarks;

      return {
        ...state,
        fields,
        config,
        options,
        benchmarks
      };
    }

    case SEGMENT_SAVE_SUCCESS: {
      const { fields, options, benchmarks, config } = state;

      if (!get(action, 'segment.isBenchmark', false)) {
        const hasEntry = findIndex(
          options.segments,
          segment => segment.uuid === action.segment.uuid
        );
        let newOptions = options.segments;
        const newOption = {
          ...action.segment,
          value: action.segment.filters || '',
          label: action.segment.name || ''
        };
        if (hasEntry > -1) {
          newOptions[hasEntry] = newOption;
        } else {
          newOptions = [...newOptions, newOption];
        }

        const segments = newOptions.filter(s => !s.isBenchmark);

        fields.segments.options = segments;
        config.segments.options = segments;
        options.segments = segments;

        return {
          ...state,
          fields,
          options,
          config,
          benchmarks
        };
      }
      const hasEntry = findIndex(
        benchmarks,
        segment => segment.uuid === action.segment.uuid
      );
      let newOptions = benchmarks;
      const newOption = {
        ...action.segment,
        value: action.segment.filters || '',
        label: action.segment.name || ''
      };
      if (hasEntry > -1) {
        newOptions[hasEntry] = newOption;
      } else {
        newOptions = [...newOptions, newOption];
      }

      return {
        ...state,
        benchmarks: newOptions
      };
    }

    case BENCHMARK_SELECTED: {
      const { benchmarks } = state;
      return {
        ...state,
        fields: { ...state.fields, benchmark: { value: action.uuid } },
        benchmarks: benchmarks.map(b => ({
          ...b,
          selected: b.uuid === action.uuid
        }))
      };
    }

    default:
      return state;
  }
}

export const selectBenchmarks = state =>
  get(state, ['filters', 'benchmarks'], []);
export const selectCategories = state =>
  get(state, ['user', 'allowedDataCategories'], []);
export const selectGroupings = state =>
  get(state, ['filters', 'groupings'], []);
export const selectFields = state => get(state, ['filters', 'fields'], {});
export const selectFilters = state => get(state, ['filters'], {});
export const selectSegments = state =>
  get(state, ['filters', 'fields', 'segments', 'options'], []);
export const selectConfig = state => get(state, ['filters', 'config'], {});
export const selectFilterValues = state => {
  const attrs = get(state, ['filters', 'fields'], {});
  const result = {};
  forEach(
    attrs,
    (attr, key) => {
      if (!isEmpty(attr.value)) {
        result[key] = { ...attr.value, id: key };
      }
    },
    {}
  );
  return result;
};
